// Position-in-source detection helpers: determines whether a byte offset
// falls inside a string literal, line comment, or block comment.
// Used by `editorContextProjection.ts` to classify EditorContextPositionKind before
// attempting clause / token analysis.

import {
  skipBacktickId,
  skipBlockComment,
  skipBracketedId,
  skipDoubleQuoted,
  skipSingleQuoted,
  skipToEol,
  tryDollarQuoted,
} from './scanPrimitives';

/** Returns true iff `offset` falls strictly inside a `-- …` line comment.
 *  `from` starts the scan somewhere other than the buffer start — used when the
 *  analysis window is a nested region such as a PG routine body. */
export function isInsideLineComment(source: string, offset: number, from = 0): boolean {
  let i = from;
  while (i < offset) {
    const c = source.charCodeAt(i);
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) {
      let j = i + 2;
      while (j < source.length) {
        const ch = source.charCodeAt(j);
        if (ch === 0x0a || ch === 0x0d) break;
        j += 1;
      }
      if (offset > i + 1 && offset < j) return true;
      i = j;
      continue;
    }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) {
      i = skipBlockComment(source, i + 2);
      continue;
    }
    if (c === 0x27) { i = skipSingleQuoted(source, i + 1); continue; }
    if (c === 0x22) { i = skipDoubleQuoted(source, i + 1); continue; }
    i += 1;
  }
  return false;
}

/** Returns true iff `offset` falls strictly inside a `/* … *\/` block comment.
 *  See `isInsideLineComment` for `from`. */
export function isInsideBlockComment(source: string, offset: number, from = 0): boolean {
  let i = from;
  while (i < offset) {
    const c = source.charCodeAt(i);
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) {
      i = skipToEol(source, i + 2);
      continue;
    }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) {
      const commentEnd = skipBlockComment(source, i + 2);
      if (offset > i + 1 && offset < commentEnd) return true;
      i = commentEnd;
      continue;
    }
    if (c === 0x27) { i = skipSingleQuoted(source, i + 1); continue; }
    if (c === 0x22) { i = skipDoubleQuoted(source, i + 1); continue; }
    i += 1;
  }
  return false;
}

/** Returns true iff `offset` falls strictly inside a string literal.
 *  Handles N'…' (MSSQL), dollar-quoted `$$…$$` (PG), standard `'…'`.
 *  Quoted identifiers (`[…]`, `"…"`, `` `…` ``) are NOT strings. */
export function isInsideStringLiteral(
  source: string,
  offset: number,
  engine: string,
  from = 0,
): boolean {
  let i = from;
  while (i < offset) {
    const c = source.charCodeAt(i);
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) {
      i = skipToEol(source, i + 2);
      continue;
    }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) {
      i = skipBlockComment(source, i + 2);
      continue;
    }
    // N' / n' prefix (MSSQL)
    if (
      (c === 0x4e || c === 0x6e) &&
      source.charCodeAt(i + 1) === 0x27
    ) {
      const end = skipSingleQuoted(source, i + 2);
      if (offset > i + 1 && offset < end) return true;
      i = end;
      continue;
    }
    // PG dollar-quoted
    if (engine === 'postgres' && c === 0x24) {
      const end = tryDollarQuoted(source, i);
      if (end !== -1) {
        if (offset > i && offset < end) return true;
        i = end;
        continue;
      }
    }
    // Standard single-quoted
    if (c === 0x27) {
      const end = skipSingleQuoted(source, i + 1);
      if (offset > i && offset < end) return true;
      i = end;
      continue;
    }
    // Quoted identifiers (not strings)
    if (c === 0x22) { i = skipDoubleQuoted(source, i + 1); continue; }
    if ((engine === 'mssql' || engine === 'sqlite') && c === 0x5b) {
      i = skipBracketedId(source, i + 1);
      continue;
    }
    if (engine === 'mysql' && c === 0x60) {
      i = skipBacktickId(source, i + 1);
      continue;
    }
    i += 1;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Dollar-quoted routine bodies
// ---------------------------------------------------------------------------

export type DollarQuotedBody = {
  /** First offset INSIDE the body (just past the opening `$tag$`). */
  readonly bodyStart: number;
  /** Offset of the closing `$tag$`, or the buffer end while it is unwritten. */
  readonly bodyEnd: number;
};

/**
 * Returns the innermost dollar-quoted region (`$$ … $$` / `$tag$ … $tag$`)
 * containing `cursor`, or null.
 *
 * PG routine bodies are written this way, and to every scanner in this file a
 * body looks exactly like a very long string literal. That is right for
 * `E'…'`-style data but wrong for a body: the text inside is SQL the user
 * expects completion in, so callers use this to move their analysis window
 * inside the body instead of treating the whole thing as opaque.
 *
 * An unterminated body — the shape while the user is still typing it — reports
 * `bodyEnd` at the buffer end rather than declining, so completion works before
 * the closing tag exists.
 */
export function findEnclosingDollarQuotedBody(
  source: string,
  cursor: number,
): DollarQuotedBody | null {
  let i = 0;
  while (i < source.length) {
    // Skip ordinary literals and comments: a `$$` inside `'costs $$$'` or a
    // comment is text, and treating it as a body opener used to hijack the
    // analysis window for the whole rest of the buffer.
    const c = source.charCodeAt(i);
    if (c === 0x2d && source.charCodeAt(i + 1) === 0x2d) { i = skipToEol(source, i + 2); continue; }
    if (c === 0x2f && source.charCodeAt(i + 1) === 0x2a) { i = skipBlockComment(source, i + 2); continue; }
    if (c === 0x27) { i = skipSingleQuoted(source, i + 1); continue; }
    if (c !== 0x24) {
      i += 1;
      continue;
    }
    const end = tryDollarQuoted(source, i);
    if (end === -1) {
      i += 1;
      continue;
    }
    // Only a ROUTINE BODY moves the analysis window. `AS $$ … $$` (CREATE
    // FUNCTION / PROCEDURE) and `DO $$ … $$` are bodies; a dollar-quoted string
    // used as DATA (`SET note = $$ hello $$`) is a literal, and analyzing its
    // contents as SQL would flood the user with completions inside their text.
    if (!isRoutineBodyIntroducer(source, i)) {
      i = end;
      continue;
    }
    // `tryDollarQuoted` returns one past the CLOSING tag (or the buffer end
    // when there is none); re-read the opening tag to find where the body
    // starts and, from its length, where the closing tag begins.
    const tagEnd = source.indexOf('$', i + 1) + 1;
    const tagLength = tagEnd - i;
    const bodyEnd = end === source.length && !source.endsWith(source.slice(i, tagEnd))
      ? source.length
      : end - tagLength;
    // `>=` at the start: the caret sitting immediately after the opening tag is
    // already in the body, and asking for completions there is the first thing
    // that happens after typing `AS $$`.
    if (cursor >= tagEnd && cursor <= bodyEnd) return { bodyStart: tagEnd, bodyEnd };
    i = end;
  }
  return null;
}

/** True when the word before the dollar tag at `at` introduces a routine body
 *  (`… LANGUAGE sql AS $$`, `DO $$`). */
function isRoutineBodyIntroducer(source: string, at: number): boolean {
  let i = at;
  while (i > 0 && /\s/.test(source[i - 1] ?? '')) i -= 1;
  let j = i;
  while (j > 0 && /[A-Za-z_]/.test(source[j - 1] ?? '')) j -= 1;
  const word = source.slice(j, i).toUpperCase();
  return word === 'AS' || word === 'DO';
}
